import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { MailCheck, Clock, ShieldCheck, Send, Loader2, Phone, MessageCircle, CalendarCheck } from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { FadeUp, Chapter, CTAButton, EASE } from "@/components/Reveal";
import { PRICES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
  "w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-sm text-ink placeholder:text-muted-foreground/60 transition-[border-color,box-shadow] duration-200 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20";

const INFO = [
  {
    icon: Clock,
    title: "Snelle reactie",
    text: "Wij reageren op werkdagen binnen 24 uur op uw bericht.",
  },
  {
    icon: ShieldCheck,
    title: "Zorgvuldig met uw gegevens",
    text: "Uw gegevens gebruiken wij uitsluitend om uw vraag of aanvraag te behandelen. Zie onze privacyverklaring.",
  },
  {
    icon: CalendarCheck,
    title: "Direct een label nodig?",
    text: "Gebruik het aanvraagformulier, dan heeft u binnen twee minuten een complete aanvraag verstuurd.",
  },
];

const initialForm = {
  naam: "",
  email: "",
  telefoon: "",
  onderwerp: "",
  adres: "",
  postcode: "",
  plaats: "",
  woningtype: "",
  woonoppervlakte: "",
  gewenste_datum: "",
  bericht: "",
};

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const valid =
    form.naam.trim() && /\S+@\S+\.\S+/.test(form.email) && form.telefoon.trim().length >= 8 && form.onderwerp;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || loading) return;
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Verzenden is niet gelukt. Probeer het later opnieuw.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Contact | Energielabel laten maken in Rotterdam | Directelabels"
        description="Vraag een offerte aan of stel uw vraag over het energielabel. Directelabels reageert op werkdagen binnen 24 uur. Actief in Rotterdam en Zuid-Holland."
        path="/contact"
      />

      <section data-testid="contact-hero" className="mx-auto max-w-7xl px-5 pb-16 pt-14 md:px-8 md:pt-24">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="text-xs font-bold uppercase tracking-[0.24em] text-primary"
        >
          Contact
        </motion.p>
        <h1 data-testid="contact-title" className="mt-6 font-display text-4xl font-semibold leading-tight tracking-tighter text-ink md:text-6xl">
          <span className="block overflow-hidden pb-1">
            <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.15, ease: EASE }}>
              Wij helpen u graag.
            </motion.span>
          </span>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.45, ease: EASE }}
          className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg"
        >
          Vraag direct een offerte aan of stel uw vraag over het energielabel, de planning of onze werkwijze. Vul het
          formulier in en wij nemen zo snel mogelijk contact met u op.
        </motion.p>
      </section>

      <section data-testid="contact-content" className="mx-auto max-w-7xl px-5 pb-24 md:px-8 md:pb-32">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <FadeUp>
              <div className="rounded-3xl border border-black/5 bg-white p-6 shadow-sm md:p-10">
                {done ? (
                  <div data-testid="contact-success" className="py-10 text-center">
                    <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-energy-pale text-primary">
                      <MailCheck size={30} />
                    </span>
                    <h2 className="mt-6 font-display text-2xl font-medium tracking-tight text-ink">Bericht verzonden</h2>
                    <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-muted-foreground md:text-base">
                      Bedankt voor uw bericht. Wij reageren op werkdagen binnen 24 uur.
                    </p>
                    <CTAButton to="/aanvragen" testid="contact-success-cta" className="mt-8">
                      Direct een label aanvragen
                    </CTAButton>
                  </div>
                ) : (
                  <form onSubmit={submit} data-testid="contact-form">
                    <h2 className="font-display text-2xl font-medium tracking-tight text-ink">Stuur ons een bericht</h2>
                    <div className="mt-8 grid gap-5 sm:grid-cols-2">
                      <div>
                        <label htmlFor="contact-naam" className="mb-2 block text-sm font-bold text-ink">Naam *</label>
                        <input id="contact-naam" data-testid="contact-input-naam" required className={inputClass} value={form.naam} onChange={set("naam")} placeholder="Uw naam" autoComplete="name" />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="mb-2 block text-sm font-bold text-ink">E-mailadres *</label>
                        <input id="contact-email" data-testid="contact-input-email" type="email" required className={inputClass} value={form.email} onChange={set("email")} placeholder="u@voorbeeld.nl" autoComplete="email" />
                      </div>
                      <div>
                        <label htmlFor="contact-telefoon" className="mb-2 block text-sm font-bold text-ink">Telefoonnummer *</label>
                        <input id="contact-telefoon" data-testid="contact-input-telefoon" type="tel" required className={inputClass} value={form.telefoon} onChange={set("telefoon")} placeholder="06 - 12 34 56 78" autoComplete="tel" />
                      </div>
                      <div>
                        <label htmlFor="contact-onderwerp" className="mb-2 block text-sm font-bold text-ink">Onderwerp *</label>
                        <select id="contact-onderwerp" data-testid="contact-select-onderwerp" required className={inputClass} value={form.onderwerp} onChange={set("onderwerp")}>
                          <option value="">Kies een onderwerp</option>
                          <option value="Energielabel aanvragen">Energielabel aanvragen</option>
                          <option value="Offerte aanvragen">Offerte aanvragen</option>
                          <option value="Zakelijke samenwerking">Zakelijke samenwerking</option>
                          <option value="Vraag over tarieven">Vraag over tarieven</option>
                          <option value="Overig">Overig</option>
                        </select>
                      </div>
                      <div className="sm:col-span-2">
                        <label htmlFor="contact-adres" className="mb-2 block text-sm font-bold text-ink">Adres van de woning</label>
                        <input id="contact-adres" data-testid="contact-input-adres" className={inputClass} value={form.adres} onChange={set("adres")} placeholder="Straatnaam en huisnummer" autoComplete="street-address" />
                      </div>
                      <div>
                        <label htmlFor="contact-postcode" className="mb-2 block text-sm font-bold text-ink">Postcode</label>
                        <input id="contact-postcode" data-testid="contact-input-postcode" className={inputClass} value={form.postcode} onChange={set("postcode")} placeholder="3011 AA" autoComplete="postal-code" />
                      </div>
                      <div>
                        <label htmlFor="contact-plaats" className="mb-2 block text-sm font-bold text-ink">Plaats</label>
                        <input id="contact-plaats" data-testid="contact-input-plaats" className={inputClass} value={form.plaats} onChange={set("plaats")} placeholder="Rotterdam" />
                      </div>
                      <div>
                        <label htmlFor="contact-woningtype" className="mb-2 block text-sm font-bold text-ink">Woningtype</label>
                        <select id="contact-woningtype" data-testid="contact-select-woningtype" className={inputClass} value={form.woningtype} onChange={set("woningtype")}>
                          <option value="">Kies een woningtype</option>
                          {PRICES.map((p) => (
                            <option key={p.slug} value={p.type}>{p.type}</option>
                          ))}
                          <option value="Maisonnette">Maisonnette</option>
                          <option value="Anders / weet ik niet">Anders / weet ik niet</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor="contact-oppervlakte" className="mb-2 block text-sm font-bold text-ink">Geschatte woonoppervlakte (m²)</label>
                        <input id="contact-oppervlakte" data-testid="contact-input-woonoppervlakte" type="number" min="10" className={inputClass} value={form.woonoppervlakte} onChange={set("woonoppervlakte")} placeholder="Bijv. 110" />
                      </div>
                      <div className="sm:col-span-2">
                        <label htmlFor="contact-datum" className="mb-2 block text-sm font-bold text-ink">Gewenste datum opname</label>
                        <input id="contact-datum" data-testid="contact-input-datum" type="date" className={inputClass} value={form.gewenste_datum} onChange={set("gewenste_datum")} min={new Date().toISOString().split("T")[0]} />
                      </div>
                      <div className="sm:col-span-2">
                        <label htmlFor="contact-bericht" className="mb-2 block text-sm font-bold text-ink">Eventuele opmerkingen</label>
                        <textarea id="contact-bericht" data-testid="contact-textarea-bericht" rows={4} className={inputClass} value={form.bericht} onChange={set("bericht")} placeholder="Waar kunnen wij u mee helpen?" />
                      </div>
                    </div>
                    <button
                      type="submit"
                      data-testid="contact-submit-button"
                      disabled={!valid || loading}
                      className="mt-8 inline-flex items-center gap-2 rounded-full bg-primary px-8 py-3.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-energy-light disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={16} />}
                      {loading ? "Verzenden..." : "Bericht versturen"}
                    </button>
                  </form>
                )}
              </div>
            </FadeUp>
          </div>

          <div className="lg:col-span-5">
            <FadeUp>
              <div data-testid="contact-direct" className="rounded-3xl bg-energy-deep p-8 text-white">
                <h2 className="font-display text-xl font-medium tracking-tight">Liever direct contact?</h2>
                <p className="mt-2 text-sm leading-relaxed text-white/70">
                  Bel of app ons voor een snelle reactie op uw vraag.
                </p>
                <div className="mt-6 space-y-3">
                  <a
                    href="tel:+31657371945"
                    data-testid="contact-bel-button"
                    className="flex items-center justify-center gap-2 rounded-full bg-white px-6 py-3.5 text-sm font-bold text-energy-deep transition-colors duration-200 hover:bg-energy-pale"
                  >
                    <Phone size={16} />
                    Bel direct: 06 - 5737 1945
                  </a>
                  <a
                    href="https://wa.me/31657371945"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-whatsapp-button"
                    className="flex items-center justify-center gap-2 rounded-full border border-white/25 px-6 py-3.5 text-sm font-bold text-white transition-colors duration-200 hover:border-white/60"
                  >
                    <MessageCircle size={16} />
                    WhatsApp ons
                  </a>
                  <a
                    href="mailto:info@directelabels.nl"
                    data-testid="contact-mail-button"
                    className="flex items-center justify-center gap-2 rounded-full border border-white/25 px-6 py-3.5 text-sm font-bold text-white transition-colors duration-200 hover:border-white/60"
                  >
                    <MailCheck size={16} />
                    Mail ons: info@directelabels.nl
                  </a>
                </div>
              </div>
            </FadeUp>

            <div className="mt-8 space-y-4">
              {INFO.map((item, i) => (
                <FadeUp key={item.title} delay={i * 0.1}>
                  <div data-testid={`contact-info-${i}`} className="flex items-start gap-4 rounded-2xl border border-black/5 bg-white p-6">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-energy-pale text-primary">
                      <item.icon size={20} />
                    </span>
                    <div>
                      <h3 className="font-display text-base font-semibold text-ink">{item.title}</h3>
                      <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{item.text}</p>
                    </div>
                  </div>
                </FadeUp>
              ))}
            </div>

            <FadeUp delay={0.35}>
              <div data-testid="contact-details-placeholder" className="mt-8 rounded-2xl border border-dashed border-primary/40 bg-energy-pale/60 p-6">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-primary">Bedrijfsgegevens</p>
                <ul className="mt-3 space-y-2 text-sm font-medium text-ink/80">
                  <li>
                    E-mailadres:{" "}
                    <a href="mailto:info@directelabels.nl" data-testid="contact-mail-link" className="font-semibold text-primary underline decoration-primary/40 underline-offset-2 hover:text-energy-light">
                      info@directelabels.nl
                    </a>
                  </li>
                  <li>KvK-nummer: <span className="italic text-muted-foreground">volgt binnenkort</span></li>
                </ul>
                <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
                  Het KvK-nummer wordt vervangen door de definitieve bedrijfsgegevens zodra deze beschikbaar zijn.
                </p>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>
    </>
  );
}
